import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Badge } from "../../../components/SystemComponent/Bagde";
import {
  MdExtension,
  MdLayers,
  MdInventory,
  MdCheckCircle,
  MdCancel,
  MdHourglassBottom,
  MdTrendingUp,
  MdTrendingDown,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

export const CompanyDashboard = () => {
  const { companyId } = useParams();
  const api = useMemo(() => createApi(), []);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    api.companyModuleSubscription
      .getDashbordById(Number(companyId))
      .then((r) => {
        if (r?.ok) setData(r.data.data);
      })
      .finally(() => setLoading(false));
  }, [api, companyId]);

  const summary = useMemo(() => {
    if (!data) return null;

    const modules = data.modules ?? [];
    const items = modules.flatMap((m: any) => m.items ?? []);

    const now = new Date();

    return {
      moduleCount: modules.length,
      packageCount: new Set(modules.map((m: any) => m.packageId)).size,
      itemCount: items.length,
      activeModules: modules.filter((m: any) => new Date(m.expireDate) > now)
        .length,
      expiredModules: modules.filter((m: any) => new Date(m.expireDate) <= now)
        .length,
      totalDefined: items.reduce(
        (a: number, b: any) => a + (b.packageDefinedAmount ?? 0),
        0
      ),
      totalUsed: items.reduce(
        (a: number, b: any) => a + (b.usedAmount ?? 0),
        0
      ),
      totalRemaining: items.reduce(
        (a: number, b: any) => a + (b.remainingAmount ?? 0),
        0
      ),
    };
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.modules.flatMap((m: any) =>
      (m.items ?? []).map((i: any) => ({
        moduleName: m.moduleName,
        moduleCode: m.moduleCode,
        moduleType: m.moduleType,
        packageTitle: m.packageTitle,
        expireDate: m.expireDate,
        confirmStatus: m.confirmStatus,
        ...i,
      }))
    );
  }, [data]);

  const columns = useMemo<any[]>(
    () => [
      {
        key: "module",
        title: "Module",
        minWidth: 200,
        accessor: (r: any) =>
          r.moduleName + `(${r.moduleType === 1 ? "Core" : "Extra"})`,
      },
      {
        key: "package",
        title: "Package",
        minWidth: 200,
        accessor: (r: any) => r.packageTitle,
      },
      {
        key: "item",
        title: "Item",
        minWidth: 220,
        accessor: (r: any) => r.packageItemDescription,
      },
      {
        key: "defined",
        title: "Defined",
        minWidth: 100,
        accessor: (r: any) => r.packageDefinedAmount,
      },
      {
        key: "total",
        title: "Total",
        minWidth: 100,
        accessor: (r: any) => r.totalAmount,
      },
      {
        key: "used",
        title: "Used",
        minWidth: 100,
        accessor: (r: any) => r.usedAmount,
      },
      {
        key: "remaining",
        title: "Remaining",
        minWidth: 120,
        accessor: (r: any) => r.remainingAmount,
        render: (_: any, v: number) => (
          <Badge
            label={String(v)}
            color={v > 0 ? "#16A34A" : "#DC2626"}
            bg={v > 0 ? "rgba(22,163,74,.18)" : "rgba(220,38,38,.18)"}
          />
        ),
      },
      {
        key: "expire",
        title: "Expire",
        minWidth: 140,
        accessor: (r: any) => r.expireDate,
        render: (_: any, v: string) => {
          const expired = new Date(v) <= new Date();
          return (
            <Badge
              label={expired ? "Expired" : "Active"}
              color={expired ? "#DC2626" : "#16A34A"}
              bg={expired ? "rgba(220,38,38,.18)" : "rgba(22,163,74,.18)"}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <div style={{ padding: 20 }}>
      <div style={S.header}>
        <div>
          <div style={S.title}>{data?.companyName ?? ""}</div>
          <div style={S.subTitle}>All modules</div>
        </div>
      </div>
      {summary && (
        <div style={S.badgeCard}>
          <div style={S.badgeGrid}>
            <Badge
              icon={MdExtension}
              label="Modules"
              value={summary.moduleCount}
              color="#6366F1"
              bg="rgba(99,102,241,.18)"
            />
            <Badge
              icon={MdLayers}
              label="Packages"
              value={summary.packageCount}
              color="#7C3AED"
              bg="rgba(124,58,237,.18)"
            />
            <Badge
              icon={MdInventory}
              label="Items"
              value={summary.itemCount}
              color="#0EA5E9"
              bg="rgba(14,165,233,.18)"
            />
            <Badge
              icon={MdCheckCircle}
              label="Active"
              value={summary.activeModules}
              color="#16A34A"
              bg="rgba(22,163,74,.18)"
            />
            <Badge
              icon={MdCancel}
              label="Expired"
              value={summary.expiredModules}
              color="#DC2626"
              bg="rgba(220,38,38,.18)"
            />
            <Badge
              icon={MdTrendingUp}
              label="Total Used"
              value={summary.totalUsed}
              color="#F97316"
              bg="rgba(249,115,22,.18)"
            />
            <Badge
              icon={MdTrendingDown}
              label="Remaining"
              value={summary.totalRemaining}
              color="#22C55E"
              bg="rgba(34,197,94,.18)"
            />
            <Badge
              icon={MdHourglassBottom}
              label="Defined"
              value={summary.totalDefined}
              color="#64748B"
              bg="rgba(100,116,139,.18)"
            />
          </div>
        </div>
      )}

      <Table
        tableKey="company-dashboard"
        loading={loading}
        data={rows}
        columns={columns}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  badgeCard: {
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  badgeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
    gap: 12,
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
};
